import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DollarSign,
  Leaf,
  TreePine,
  Car,
  TrendingUp,
  Award,
  BarChart3,
  ShoppingBag,
  Loader2,
  Sparkles,
  Store,
  Users,
  Package,
  Globe,
  Trophy,
  Medal
} from 'lucide-react';
import { impact } from '../../utils/api';

// Platform Summary Card
function PlatformSummaryCard({ summary }) {
  if (!summary) return null;

  return (
    <Card className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white overflow-hidden">
      <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24" />
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full -ml-16 -mb-16" />
      
      <CardHeader className="relative">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Globe className="w-6 h-6" />
          </div>
          <div>
            <CardTitle className="text-white">Platform Impact</CardTitle>
            <CardDescription className="text-purple-200">Total environmental contribution</CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="relative">
        {summary.diversion && (
          <div className="bg-white/10 rounded-xl p-4 mb-6 backdrop-blur-sm flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="platform-diversion-strip">
            <div className="flex items-center gap-2">
              <Leaf className="w-7 h-7 text-green-300" />
              <div>
                <div className="text-2xl font-bold">{(summary.diversion.lbs_diverted || 0).toLocaleString()} lbs</div>
                <div className="text-xs text-purple-200">Food Diverted From Landfill</div>
              </div>
            </div>
            <div className="text-emerald-100">
              → ~<strong>{(summary.diversion.meals_provided || 0).toLocaleString()}</strong> meals to tables
              <span className="text-purple-200/70"> · </span>
              <strong>{(summary.diversion.charitable_meals_funded || 0).toLocaleString()}</strong> charitable meals funded
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur-sm">
            <DollarSign className="w-8 h-8 mx-auto mb-2 text-emerald-300" />
            <div className="text-2xl font-bold">${summary.total_savings.toLocaleString()}</div>
            <div className="text-xs text-purple-200">Total Savings</div>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur-sm">
            <Leaf className="w-8 h-8 mx-auto mb-2 text-green-300" />
            <div className="text-2xl font-bold">{summary.total_ghg_averted.toLocaleString()} kg</div>
            <div className="text-xs text-purple-200">CO₂ Averted</div>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur-sm">
            <TreePine className="w-8 h-8 mx-auto mb-2 text-teal-300" />
            <div className="text-2xl font-bold">{summary.trees_equivalent}</div>
            <div className="text-xs text-purple-200">Trees Equivalent</div>
          </div>
          <div className="bg-white/10 rounded-xl p-4 text-center backdrop-blur-sm">
            <Car className="w-8 h-8 mx-auto mb-2 text-blue-300" />
            <div className="text-2xl font-bold">{summary.cars_off_road_days}</div>
            <div className="text-xs text-purple-200">Car-Free Days</div>
          </div>
        </div>
        
        <div className="grid grid-cols-4 gap-4 pt-4 border-t border-white/20">
          <div className="text-center">
            <div className="text-xl font-bold">{summary.transaction_count.toLocaleString()}</div>
            <div className="text-xs text-purple-200">Transactions</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold">{summary.items_saved.toLocaleString()}</div>
            <div className="text-xs text-purple-200">Items Rescued</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold">{summary.unique_consumers}</div>
            <div className="text-xs text-purple-200">Active Consumers</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold">{summary.unique_retailers}</div>
            <div className="text-xs text-purple-200">Active Retailers</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Top Retailers Leaderboard
function TopRetailersLeaderboard({ retailers, sortBy, onSortChange }) {
  const getMedalIcon = (rank) => {
    switch (rank) {
      case 1: return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 2: return <Medal className="w-5 h-5 text-gray-400" />;
      case 3: return <Medal className="w-5 h-5 text-amber-600" />;
      default: return <span className="w-5 h-5 flex items-center justify-center text-gray-500 font-medium">{rank}</span>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5 text-blue-500" />
              Top Retailers by Impact
            </CardTitle>
            <CardDescription>Leading the sustainability charge</CardDescription>
          </div>
          <Select value={sortBy} onValueChange={onSortChange} data-testid="impact-retailer-sort-select">
            <SelectTrigger className="w-40" data-testid="impact-retailer-sort-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ghg" data-testid="impact-sort-ghg">By CO₂ Averted</SelectItem>
              <SelectItem value="savings" data-testid="impact-sort-savings">By Savings</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {retailers.length > 0 ? (
          <div className="space-y-3">
            {(retailers || []).map((retailer, i) => (
              <div 
                key={i} 
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  retailer.rank <= 3 ? 'bg-gradient-to-r from-amber-50 to-yellow-50' : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  {getMedalIcon(retailer.rank)}
                  <div>
                    <div className="font-medium">{retailer.retailer_name}</div>
                    <div className="text-xs text-gray-500">
                      {retailer.transaction_count} orders · {retailer.items_sold} items
                    </div>
                  </div>
                </div>
                <div className="flex gap-6 text-right">
                  <div>
                    <div className="font-bold text-emerald-600">${retailer.savings.toLocaleString()}</div>
                    <div className="text-xs text-gray-400">savings</div>
                  </div>
                  <div>
                    <div className="font-bold text-green-600">{retailer.ghg_averted.toFixed(1)} kg</div>
                    <div className="text-xs text-gray-400">CO₂</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Store className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No retailer data yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Consumer Leaderboard
function ConsumerLeaderboard({ consumers }) {
  const getMedalIcon = (rank) => {
    switch (rank) {
      case 1: return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 2: return <Medal className="w-5 h-5 text-gray-400" />;
      case 3: return <Medal className="w-5 h-5 text-amber-600" />;
      default: return <span className="w-5 h-5 flex items-center justify-center text-gray-500 font-medium">{rank}</span>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-500" />
          Top Savers
        </CardTitle>
        <CardDescription>Consumers making the biggest impact</CardDescription>
      </CardHeader>
      <CardContent>
        {consumers.length > 0 ? (
          <div className="space-y-3">
            {(consumers || []).map((consumer, i) => (
              <div 
                key={i} 
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  consumer.rank <= 3 ? 'bg-gradient-to-r from-purple-50 to-pink-50' : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-3">
                  {getMedalIcon(consumer.rank)}
                  <div>
                    <div className="font-medium">{consumer.display_name}</div>
                    <div className="text-xs text-gray-500">{consumer.transaction_count} orders</div>
                  </div>
                </div>
                <div className="flex gap-6 text-right">
                  <div>
                    <div className="font-bold text-emerald-600">${consumer.savings.toLocaleString()}</div>
                    <div className="text-xs text-gray-400">saved</div>
                  </div>
                  <div>
                    <div className="font-bold text-green-600">{consumer.ghg_averted.toFixed(1)} kg</div>
                    <div className="text-xs text-gray-400">CO₂</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No consumer data yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Main Admin Impact Dashboard
export default function AdminImpactDashboard() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [topRetailers, setTopRetailers] = useState([]);
  const [consumerLeaderboard, setConsumerLeaderboard] = useState([]);
  const [retailerSortBy, setRetailerSortBy] = useState('ghg');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadImpactData();
  }, []);

  useEffect(() => {
    loadTopRetailers();
  }, [retailerSortBy]);

  const loadImpactData = async () => {
    setLoading(true);
    try {
      const [summaryRes, retailersRes, consumersRes] = await Promise.all([
        impact.platformSummary(),
        impact.topRetailers(10, retailerSortBy),
        impact.consumerLeaderboard(10)
      ]);
      
      setSummary(summaryRes.data);
      setTopRetailers(retailersRes.data.retailers || []);
      setConsumerLeaderboard(consumersRes.data.leaderboard || []);
    } catch (error) {
      console.error('Failed to load impact data:', error);
      setSummary({
        total_savings: 0,
        total_ghg_averted: 0,
        transaction_count: 0,
        items_saved: 0,
        unique_consumers: 0,
        unique_retailers: 0,
        trees_equivalent: 0,
        cars_off_road_days: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTopRetailers = async () => {
    try {
      const response = await impact.topRetailers(10, retailerSortBy);
      setTopRetailers(response.data.retailers || []);
    } catch (error) {
      console.error('Failed to load top retailers:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-500" />
            Platform Impact Analytics
          </h2>
          <p className="text-gray-500">Track savings and environmental impact across the platform</p>
        </div>
        <Button variant="outline" onClick={loadImpactData} data-testid="admin-impact-refresh-btn">
          <TrendingUp className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Platform Summary */}
      <PlatformSummaryCard summary={summary} />

      {/* Average Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <DollarSign className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-emerald-600">${summary?.avg_savings_per_order || 0}</div>
              <div className="text-sm text-gray-500">Avg. Savings/Order</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Leaf className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-600">{summary?.avg_ghg_per_order || 0} kg</div>
              <div className="text-sm text-gray-500">Avg. CO₂/Order</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <ShoppingBag className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-blue-600">
                {summary?.transaction_count ? (summary.items_saved / summary.transaction_count).toFixed(1) : 0}
              </div>
              <div className="text-sm text-gray-500">Avg. Items/Order</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <Award className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-purple-600">
                {summary?.unique_consumers ? (summary.total_savings / summary.unique_consumers).toFixed(0) : 0}
              </div>
              <div className="text-sm text-gray-500">Avg. Savings/Consumer</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Leaderboards */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="retailers" data-testid="impact-tab-retailers">Top Retailers</TabsTrigger>
          <TabsTrigger value="consumers" data-testid="impact-tab-consumers">Top Consumers</TabsTrigger>
        </TabsList>

        <TabsContent value="retailers" className="mt-6">
          <TopRetailersLeaderboard 
            retailers={topRetailers} 
            sortBy={retailerSortBy}
            onSortChange={setRetailerSortBy}
          />
        </TabsContent>

        <TabsContent value="consumers" className="mt-6">
          <ConsumerLeaderboard consumers={consumerLeaderboard} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
